import styled from "@emotion/styled"
import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import { PText } from "@porsche-design-system/components-react"
import { spacingFluidMd, spacingFluidXl, spacingStaticMd } from "@porsche-design-system/components-react/emotion"
import type { Warranty } from "@slfinrtl/ui-bff-routes"
import { FormattedMessage, useIntl } from "react-intl"
import type { MessageDescriptor } from "react-intl"
import { emptyPlaceholder } from "src/common/emptyPlaceholder"
import { porscheApprovedMessages } from "src/common/messages/porscheApproved.messages"
import { Grid, GridItem, List } from "src/components"
import { PorscheApprovedCheckbox } from "./PorscheApprovedCheckbox"

export const PorscheApprovedDecisionAndVehicleWarranties = ({
	dealerDecisionPorscheApproved,
	disablementReasons,
	editMode,
	onDealerDecisionChanged,
	reportedWarranties,
}: {
	dealerDecisionPorscheApproved: boolean | undefined
	disablementReasons: MessageDescriptor[]
	editMode: boolean
	onDealerDecisionChanged: (checked: boolean) => void
	reportedWarranties: { remainingDurationInMonths: number; warranties: Warranty[] }
}) => {
	const { formatMessage } = useIntl()
	const { locale } = useDealerContext()

	const convertDateToLocaleString = (isoString: string) =>
		new Date(isoString).toLocaleDateString(locale, {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		})

	const remainingDurationInMonths = reportedWarranties.remainingDurationInMonths
	const newCarWarranties = reportedWarranties.warranties.filter((warranty) => warranty.type === "NEW_CAR_WARRANTY")
	const usedCarWarranties = reportedWarranties.warranties.filter((warranty) => warranty.type !== "NEW_CAR_WARRANTY")

	return (
		<Grid $columnGap={{ base: spacingFluidMd, s: spacingFluidXl }}>
			<GridItem size={{ base: 12, m: 6 }}>
				{editMode && (
					<>
						<PorscheApprovedCheckbox
							label={formatMessage(porscheApprovedMessages.certified)}
							checked={dealerDecisionPorscheApproved}
							disablementReasons={disablementReasons}
							onChange={onDealerDecisionChanged}
						/>
						<SpacingStaticMedium />
					</>
				)}
				<List>
					{!editMode && (
						<List.Item
							label={<FormattedMessage {...porscheApprovedMessages.status} />}
							value={
								dealerDecisionPorscheApproved ? (
									<FormattedMessage {...porscheApprovedMessages.certified} />
								) : (
									<FormattedMessage {...porscheApprovedMessages.notCertified} />
								)
							}
						/>
					)}
					<List.Item
						label={<FormattedMessage {...porscheApprovedMessages.newCarWarranty} />}
						value={
							newCarWarranties.length > 0 ? (
								// biome-ignore lint/complexity/noUselessFragments: List.Item value expects a single element.
								<>
									{newCarWarranties.map((newCarWarranty, index) => (
										<PText key={index} weight="semibold">
											<FormattedMessage
												{...porscheApprovedMessages.dateRange}
												values={{
													endDate: convertDateToLocaleString(newCarWarranty.endDate),
													startDate: convertDateToLocaleString(newCarWarranty.startDate),
												}}
											/>
										</PText>
									))}
								</>
							) : (
								emptyPlaceholder
							)
						}
					/>
					<List.Item
						label={<FormattedMessage {...porscheApprovedMessages.porscheApproved} />}
						value={
							usedCarWarranties.length > 0 ? (
								// biome-ignore lint/complexity/noUselessFragments: List.Item value expects a single element.
								<>
									{usedCarWarranties.map((usedCarWarranty, i) => (
										<PText key={i} weight="semibold">
											<FormattedMessage
												{...porscheApprovedMessages.dateRange}
												values={{
													endDate: convertDateToLocaleString(usedCarWarranty.endDate),
													startDate: convertDateToLocaleString(usedCarWarranty.startDate),
												}}
											/>
										</PText>
									))}
								</>
							) : (
								emptyPlaceholder
							)
						}
					/>
					<List.Item
						label={<FormattedMessage {...porscheApprovedMessages.remainingWarranty} />}
						value={
							remainingDurationInMonths > 0 ? (
								<FormattedMessage
									{...porscheApprovedMessages.remainingMonths}
									values={{
										remainingWarranty: remainingDurationInMonths,
									}}
								/>
							) : (
								emptyPlaceholder
							)
						}
					/>
				</List>
			</GridItem>
		</Grid>
	)
}

const SpacingStaticMedium = styled.div`
	margin-bottom: ${spacingStaticMd};
`
